<template>
    <div
        ref="container"
        :disabled="disabled"
        :class="[$style['mds-combo-box'], classObject, sizeClass]"
        v-click-outside="hideResults"
    >
        <div
            :class="$style['mds-combo-box__multiple-select']"
            ref="wrapper"
            :aria-describedby="ariaDescription"
            :aria-disabled="disabled ? 'true' : null"
            :tabindex="!disabled ? '0' : null"
        >
            <mds-icon :class="$style['mds-combo-box__open-icon']" :name="iconName" size="small" role="presentation" />
            <div
                :class="[$style['mds-combo-box__selected-list'], focusClass]"
                :aria-labelledby="ariaLabel"
                ref="selection"
            >
                <span
                    :class="$style['mds-combo-box__placeholder']"
                    v-if="!showResults && searchTerm.length === 0 && selectedItems.length === 0"
                >
                    {{ placeholder }}
                </span>
                <mds-tag
                    v-for="(item, index) in selectedItems"
                    :id="tagId()"
                    :key="index"
                    :data-text="item.text"
                    :data-value="item.value"
                    v-show="selectedItems.length < multipleItemLimit"
                    :text="item.text"
                    :removable="disabled ? null : true"
                    :on-light="!onDark"
                    :on-dark="onDark"
                    :size="tagSize"
                    remove-icon-aria-label="Remove"
                    :class="$style['mds-combo-box__selection-tag']"
                    @mds-tag-removed="handleRemoveClick(item.value)"
                >
                </mds-tag>
                <mds-tag
                    v-show="selectedItems.length >= multipleItemLimit"
                    :id="tagId()"
                    :text="selectedItems.length + ' Items selected'"
                    :removable="disabled ? null : true"
                    :on-light="!onDark"
                    :on-dark="onDark"
                    remove-icon-aria-label="Remove"
                    :size="tagSize"
                    :class="$style['mds-combo-box__selection-tag']"
                    @mds-tag-removed="handleRemoveClick()"
                >
                </mds-tag>

                <div :class="$style['mds-combo-box__assistive-message']">
                    <span v-if="showResults && filteredList.length === 0" aria-live="assertive">{{
                        noResultsLabel
                    }}</span>
                    <span
                        v-if="showResults && filteredList.length > 0"
                        role="status"
                        aria-atomic="true"
                        aria-live="polite"
                    >
                        <slot name="mds-combo-box-result-description" :items-count="filteredList.length"
                            >{{ filteredList.length }} results are available. Use up and down arrows to review and enter
                            to select options.</slot
                        >
                    </span>
                    <span v-if="selectedItems.length > 0" aria-live="polite">
                        <slot
                            name="mds-combo-box-selection-description"
                            :selected-count="selectedItems.length"
                            :selected-items="selectedItemsInfo"
                            >Multi selection combo box. {{ selectedItems.length }} selected items:
                            {{ selectedItemsInfo }}. Use Tab to remove items or search for more items.</slot
                        >
                    </span>
                </div>

                <input
                    type="text"
                    v-bind="attrsComputed"
                    v-model="searchTerm"
                    :class="$style['mds-combo-box__input']"
                    ref="input"
                    :disabled="disabled"
                    :id="ariaInputId"
                    @input="handleInput"
                    @focus="setFocus"
                    @keydown.delete="deleteTags"
                    @click="toggleResults()"
                    @keydown.arrow-down="openResults()"
                    @keyup.enter.self.prevent="openResults()"
                    @keydown.tab="handleTab"
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-autocomplete="list"
                    autocomplete="off"
                    :aria-controls="resultsId"
                    :aria-activedescendant="ariaActiveDescendant"
                    :required="required"
                    :aria-expanded="showResults ? 'true' : 'false'"
                />
            </div>
            <div v-show="showResults">
                <ul
                    ref="results"
                    v-auto-direction="positionResults"
                    v-keyboard-navigation="handleKeyboardNavigation"
                    :class="$style['mds-combo-box__result-list']"
                    role="listbox"
                    :id="resultsId"
                >
                    <li
                        @click.prevent.stop="setItemState(item.text, item.value)"
                        v-for="(item, index) in filteredList"
                        role="option"
                        :id="`mds-combo-box__descendent-${resultsId}--${index}`"
                        :ref="`${index}`"
                        :key="`${item.value}-${index}`"
                        :aria-posinset="index + 1"
                        :aria-setsize="filteredList.length"
                        :aria-selected="isHighlighted(item, index) ? 'true' : 'false'"
                        :class="{
                            [$style['mds-combo-box__item-selected']]: isSelected(item.value),
                            [$style['mds-combo-box--highlighted']]: isHighlighted(item, index),
                            [$style['mds-combo-box--heading']]: item.value === null,
                            [$style['mds-combo-box--child']]: item.child === true,
                        }"
                        :data-text="item.text"
                        :data-value="item.value"
                    >
                        <span v-html="highlightCharacters(item.text, searchTerm, $style)"></span>
                        <div v-if="item.metadata" :class="$style['mds-combo-box__item-metadata']">
                            <span
                                v-for="(metaItem, metaIndex) in item.metadata"
                                :key="`metabelow-${metaIndex}`"
                                v-html="metaItem"
                                :class="[$style['mds-combo-box__item-metadata-item']]"
                            ></span>
                        </div>
                        <mds-icon
                            v-if="isSelected(item.value)"
                            :class="$style['mds-combo-box__selected-icon']"
                            name="check"
                            size="small"
                            role="presentation"
                        />
                    </li>
                    <li v-show="filteredList.length === 0" aria-hidden="true">{{ noResultsLabel }}</li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
    import { MdsTag } from '@mds/tag-vue3';
    import MdsIcon from '@mds/icon-vue3';
    import { MdsUniqueId } from '@mds/utils-js-vue3';
    import ComboBoxDirectives from './combo-box_directives.js';
    import { highlightCharacters } from './combo-box_filters.js';

    export default {
        name: 'MdsMultiSelect',
        inheritAttrs: false,
        mixins: [MdsUniqueId],
        props: {
            ariaDescription: {
                type: String,
            },
            ariaInputId: {
                type: String,
            },
            ariaLabel: {
                type: String,
            },
            dataSet: {
                type: Array,
            },
            description: {
                type: String,
            },
            disabled: {
                type: Boolean,
            },
            displayTop: {
                type: Boolean,
            },
            error: {
                type: Boolean,
            },
            initialSelection: {
                type: Array,
            },
            noResultsLabel: {
                type: String,
            },
            multipleItemLimit: {
                type: Number,
            },
            onDark: {
                type: Boolean,
            },
            placeholder: {
                type: String,
            },
            required: {
                type: Boolean,
            },
            resultsId: {
                type: String,
            },
            size: {
                type: String,
            },
        },
        components: {
            MdsTag,
            MdsIcon,
        },
        emits: ['input'],
        data() {
            return {
                isFocused: false,
                searchTerm: '',
                selectedItems: [],
                showResults: false,
                highlightedItem: -1,
            };
        },
        watch: {
            valueOnlyList(val) {
                if (this.initialSelection && this.isEqual(val, this.initialSelection) === false) {
                    this.$emit('input', val);
                }
            },
            initialSelection: {
                handler() {
                    this.setInitialSelection();
                },
                deep: true,
            },
        },
        computed: {
            attrsComputed() {
                // don't apply class attribute to <input>
                const { class: cssClass, ...rest } = this.$attrs;
                return rest;
            },
            ariaActiveDescendant() {
                return this.showResults && this.highlightedItem > -1
                    ? `mds-combo-box__descendent-${this.resultsId}--${this.highlightedItem}`
                    : null;
            },
            filteredList() {
                return this.dataSet.filter(item => {
                    var vm = this;
                    return item.text.toLowerCase().includes(vm.searchTerm.toLowerCase());
                });
            },
            valueOnlyList() {
                return this.selectedItems.length > 0 ? this.selectedItems.map(item => item.value) : this.selectedItems;
            },
            sizeClass() {
                return this.$style[`mds-combo-box--${this.size}`];
            },
            tagSize() {
                return this.size === 'large' || this.size === 'touch' ? 'medium' : 'small';
            },
            focusClass() {
                const obj = {};
                obj[this.$style['mds-combo-box--focus']] = this.isFocused;

                return obj;
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-component--on-dark']] = this.onDark;
                obj[this.$style['mds-combo-box--error']] = this.error;

                return obj;
            },
            iconName() {
                return this.showResults ? `caret-up` : `caret-down`;
            },
            selectedItemsInfo() {
                return this.selectedItems.map(item => item.text).join(', ');
            },
        },
        beforeMount() {
            this.setInitialSelection();
        },
        updated() {
            if (this.showResults) {
                this.positionResults(this.$refs.results);
            }
        },
        directives: {
            clickOutside: ComboBoxDirectives.clickOutside,
            autoDirection: ComboBoxDirectives.autoDirection,
            keyboardNavigation: ComboBoxDirectives.keyboardNavigation,
        },
        methods: {
            highlightCharacters,
            toggleResults() {
                if (this.showResults) {
                    this.hideResults();
                } else {
                    this.openResults();
                }
            },
            tagId() {
                return this.generateUuid(`${this.resultsId}-tag`);
            },
            isEqual(arr1, arr2) {
                if (arr1.length !== arr2.length) {
                    return false;
                }

                for (let i = arr1.length; i--; ) {
                    if (arr1[i] !== arr2[i]) {
                        return false;
                    }
                }

                return true;
            },
            setInitialSelection() {
                if (!this.initialSelection || this.initialSelection.length === 0) {
                    this.selectedItems = [];
                    return;
                }

                if (this.isEqual(this.initialSelection, this.valueOnlyList) === false) {
                    const filteredItems = [];

                    for (let i = 0; i < this.initialSelection.length; i++) {
                        const value = this.initialSelection[i];
                        filteredItems.push(this.dataSet.find(element => element.value === value));
                    }
                    this.selectedItems = filteredItems;
                }
            },
            setItemState(text, value) {
                if (value !== null) {
                    if (this.isSelected(value)) {
                        this.unselectItem(value);
                    } else {
                        this.selectItem(value);
                    }
                }
            },
            handleRemoveClick(value) {
                if (value) {
                    this.unselectItem(value);
                } else {
                    this.selectedItems = [];
                }

                // Return focus to input whenever item(s) are removed
                this.$refs.input.focus();
            },
            selectItem(value) {
                this.selectedItems.push(this.dataSet.find(element => element.value === value));
                this.searchTerm = '';
            },
            isSelected(value) {
                return this.selectedItems.find(element => element.value === value) !== undefined;
            },
            isHighlighted(item, index) {
                return this.showResults && item.value && index === this.highlightedItem;
            },
            unselectItem(value) {
                this.selectedItems.splice(
                    this.selectedItems.findIndex(item => item.value === value),
                    1
                );
            },
            scrollToItem(el, options = { block: 'nearest', inline: 'nearest' }) {
                if (el) {
                    el.scrollIntoView(options);
                }
            },
            setFocus() {
                if (!this.disabled) {
                    this.isFocused = true;
                }
            },
            removeFocus() {
                this.isFocused = false;
            },
            openResults() {
                if (this.disabled || this.showResults) {
                    return;
                }

                // Reset highlight position
                this.highlightedItem = -1;

                this.showResults = true;
                this.isFocused = true;

                this.$nextTick(() => {
                    if (this.selectedItems.length > 0) {
                        const lastSelection = this.selectedItems[this.selectedItems.length - 1];
                        const selectedIndex = this.dataSet.findIndex(item => item.value === lastSelection.value);
                        this.scrollToItem(this.$refs[selectedIndex][0]);
                    }
                });
            },
            hideResults(autoFocus = false) {
                if (!this.showResults) {
                    return;
                }

                this.highlightedItem = 0;
                this.showResults = false;
                this.isFocused = false;
                this.searchTerm = '';

                // shift focus back to wrapper element
                if (autoFocus) {
                    this.$refs.input.focus();
                }
            },
            isHeading(el) {
                return el.classList.contains(this.$style['mds-combo-box--heading']);
            },
            positionResults(el) {
                const selectionOffset = this.$refs.selection.getBoundingClientRect().height;

                if (this.displayTop) {
                    el.style.cssText = `bottom: ${selectionOffset}px`;
                } else {
                    el.style.cssText = `top: ${selectionOffset}px`;
                }
            },
            handleKeyboardNavigation(event) {
                let step;
                const isCombobox = this.$refs.container.contains(event.target);

                if (!this.showResults && isCombobox) {
                    // If for some reason, a user attempts to press enter when results aren't visible
                    if (event.key === 'Enter') {
                        event.preventDefault();
                    }

                    return;
                }

                if (event.key === 'Escape') {
                    this.hideResults(true);

                    return;
                }

                // Enter key
                if (event.key === 'Enter' && event.target.tagName !== 'BUTTON' && isCombobox) {
                    event.preventDefault();

                    if (
                        this.filteredList.length > 0 &&
                        this.highlightedItem > -1 &&
                        !this.isHeading(this.$refs[this.highlightedItem][0])
                    ) {
                        this.setItemState(
                            this.$refs[this.highlightedItem][0].dataset.text,
                            this.$refs[this.highlightedItem][0].dataset.value
                        );
                    }

                    return;
                }

                // Other keys
                if (event.key === 'ArrowUp' && this.highlightedItem > 0) {
                    step =
                        this.isHeading(this.$refs[this.highlightedItem - 1][0]) && this.highlightedItem - 2 > 0 ? 2 : 1;
                    this.highlightedItem -= step;
                } else if (event.key === 'ArrowDown' && this.highlightedItem < this.filteredList.length - 1) {
                    step = this.isHeading(this.$refs[this.highlightedItem + 1][0]) ? 2 : 1;
                    this.highlightedItem += step;
                } else if (event.key === 'Home' && this.highlightedItem > 0) {
                    this.highlightedItem = 0;
                } else if (event.key === 'End' && this.highlightedItem < this.filteredList.length - 1) {
                    this.highlightedItem = this.filteredList.length - 1;
                }

                if (this.highlightedItem > -1) {
                    this.scrollToItem(this.$refs[this.highlightedItem][0]);
                }
            },
            deleteTags(event) {
                if (event.target.value.length === 0) {
                    this.selectedItems.pop();
                }
            },
            handleInput() {
                this.highlightedItem = -1;

                if (!this.showResults) {
                    this.openResults();
                }
            },
            handleTab() {
                this.hideResults();
                this.removeFocus();
            },
        },
    };
</script>

<style lang="scss" module>
    @use './combo-box.scss';
</style>
